import { canAccessEexPath } from "../lib/eex-access";
import { navigationFor } from "../lib/navigation";
import {
  createRootRouteWithContext,
  Outlet,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { api, ApiError } from "../lib/api";
import { useAuth, type AuthUser } from "../stores/auth";
import { AppShell } from "../components/layout/app-shell";
import { useMobileUiV1State } from "../hooks/use-media-query";

export interface Ctx {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<Ctx>()({
  component: RootLayout,
});

const eexLandingFor = (user: AuthUser) => navigationFor(user).find(section => section.id === "eex")?.landingTo;

const PUBLIC_PATHS = new Set<string>(["/login"]);
const PASSWORD_PATH = "/change-password";

function RootLayout() {
  const user = useAuth((s) => s.user);
  const setUser = useAuth((s) => s.setUser);
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { presenter } = useMobileUiV1State();

  const meQuery = useQuery({
    queryKey: ["auth", "me"],
    staleTime: 60_000,
    retry: false,
    queryFn: async () => {
      try {
        const res = await api<{ user: AuthUser }>("/auth/me");
        return res.user;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
  });

  useEffect(() => {
    if (meQuery.isFetching) return;
    setUser(meQuery.data ?? null);
  }, [meQuery.data, meQuery.isFetching, setUser]);

  useEffect(() => {
    if (meQuery.isFetching) return;
    if (!meQuery.data && !PUBLIC_PATHS.has(location.pathname)) {
      navigate({ to: "/login", replace: true });
    } else if (
      meQuery.data?.mustChangePassword &&
      location.pathname !== PASSWORD_PATH
    ) {
      navigate({ to: PASSWORD_PATH, replace: true });
    } else if (meQuery.data?.eexEnabled && ["/eex", "/eex/"].includes(location.pathname)) {
      const dest = eexLandingFor(meQuery.data);
      if (dest) navigate({ to: dest, replace: true });
    } else if (meQuery.data?.role === "finance") {
      const dest = meQuery.data.eexEnabled ? eexLandingFor(meQuery.data) : null;
      if (dest && ["/", "/eex", "/eex/", "/login"].includes(location.pathname)) navigate({ to: dest, replace: true });
    } else if (
      meQuery.data &&
      !meQuery.data.tradingEnabled &&
      (location.pathname === "/grid" ||
        (location.pathname === "/" && presenter === "desktop"))
    ) {
      // Keep the existing desktop landing behavior. The mobile dashboard stays
      // reachable because it is the cross-module Phase 1 entry point.
      const dest = meQuery.data.forecastingEnabled
        ? "/forecasting"
        : meQuery.data.eexEnabled
        ? eexLandingFor(meQuery.data)
        : meQuery.data.brmEnabled
        ? "/settings/brm"
        : null;
      if (dest) navigate({ to: dest, replace: true });
    }
  }, [
    meQuery.data,
    meQuery.isFetching,
    location.pathname,
    navigate,
    presenter,
  ]);

  const identityPreferenceMutation = useMutation({
    mutationFn: (hideIdentityInHeader: boolean) =>
      api<{ user: AuthUser }>("/auth/me/preferences", {
        method: "PATCH",
        body: JSON.stringify({ hideIdentityInHeader }),
      }),
    onSuccess: (res) => {
      queryClient.setQueryData(["auth", "me"], res.user);
      setUser(res.user);
    },
  });

  async function logout() {
    try {
      await api("/auth/logout", { method: "POST" });
    } catch {}
    setUser(null);
    // Drop ALL cached query data — otherwise the previous tenant's billing/EEX data
    // lingers in the React Query cache and renders for the next user who signs in on
    // this same SPA session (no full page reload happens between logout and login).
    queryClient.clear();
    navigate({ to: "/login", replace: true });
  }

  if (meQuery.isLoading) {
    return (
      <div className="min-h-screen grid place-items-center text-muted text-xs">
        loading…
      </div>
    );
  }

  if (!user && !PUBLIC_PATHS.has(location.pathname)) {
    return null;
  }

  const isAdminPath = location.pathname.startsWith("/admin");
  const hideHeaderIdentity = Boolean(user?.hideIdentityInHeader && !isAdminPath);

  return (
    <AppShell
      user={user}
      pathname={location.pathname}
      hideHeaderIdentity={hideHeaderIdentity}
      identityPreferenceBusy={identityPreferenceMutation.isPending}
      onToggleIdentity={() =>
        user && identityPreferenceMutation.mutate(!user.hideIdentityInHeader)
      }
      onLogout={logout}
    >
      {user?.role === "finance" && location.pathname !== PASSWORD_PATH && location.pathname !== "/login" &&
        (!user.eexEnabled || !canAccessEexPath(user, location.pathname)) ? (
        <section className="p-6 space-y-2"><h1 className="text-lg font-medium">Finance access</h1>
          <p className="text-sm text-muted">{user.eexEnabled && eexLandingFor(user)
            ? "This page is not enabled for your account. Choose an available EEX tab."
            : "Your company administrator needs to enable EEX and select the tabs you can access."}</p></section>
      ) : user && location.pathname.startsWith("/eex/") && !canAccessEexPath(user, location.pathname) ? (
        <section className="p-6 space-y-2"><h1 className="text-lg font-medium">EEX access</h1><p className="text-sm text-muted">This page is not enabled for your account. Choose an available EEX tab.</p></section>
      ) : <Outlet />}
    </AppShell>
  );
}
